import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ShareStoryPanel from './ShareStoryPanel';
import supabase from '../lib/supabase';

const EditStoryModal = ({ isOpen, onClose, onSave, story }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [storyData, setStoryData] = useState(null);

  useEffect(() => {
    if (story) {
      setTitle(story.title || '');
      setDescription(story.description || '');
      setImageUrl(story.story_image_url || '');
      setImagePreview(story.story_image_url || null);
      setStoryData(story);
    }
  }, [story]);

  const handleImageUpload = async (event) => {
    try {
      setUploading(true);
      const file = event.target.files[0];
      if (!file) return;

      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Generate a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `story-images/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('storybucket')
        .upload(filePath, file);

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('storybucket')
        .getPublicUrl(filePath);

      setImageUrl(publicUrlData.publicUrl);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleVisibilityChange = (visibility) => {
    // Just update the local state, don't save to the database yet
    setStoryData(prev => ({
      ...prev,
      visibility
    }));
  };

  const handleStoryUpdate = (updatedStory) => {
    // Update the local story state
    setStoryData(updatedStory);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Please enter a title for your story');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updatedStory = {
        ...storyData,
        title,
        description,
        story_image_url: imageUrl,
      };

      await onSave(updatedStory);
    } catch (err) {
      console.error('Error saving story:', err);
      setError('Failed to save story. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-dreamy"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-display font-bold text-purple-700">
              Edit Story
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-full"
              onClick={onClose}
              type="button"
            >
              <SafeIcon icon={FiIcons.FiX} className="text-lg" />
            </motion.button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
              <p>{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-purple-700 font-medium mb-2">
                Story Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
                placeholder="Enter a captivating title..."
                disabled={saving}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-purple-700 font-medium mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
                placeholder="What is your story about?"
                rows="4"
                disabled={saving}
              />
            </div>

            {/* Story Image */}
            <div>
              <label className="block text-purple-700 font-medium mb-2">
                Story Cover Image
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="cursor-pointer bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2 justify-center">
                    <SafeIcon icon={FiIcons.FiUpload} className="text-lg" />
                    {uploading ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading || saving}
                    />
                  </label>
                  {uploading && (
                    <div className="mt-2 flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500 mr-2"></div>
                      <span className="text-purple-600 text-sm">Uploading image...</span>
                    </div>
                  )}
                  {imageUrl && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-2 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                      onClick={() => {
                        setImageUrl('');
                        setImagePreview(null);
                      }}
                      disabled={saving}
                      type="button"
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" /> Remove Image
                    </motion.button>
                  )}
                </div>
                <div>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Story cover preview"
                      className="w-full h-32 object-cover rounded-xl"
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      <SafeIcon icon={FiIcons.FiImage} className="text-3xl" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sharing Settings Section */}
            {storyData && (
              <div>
                <ShareStoryPanel 
                  story={storyData} 
                  onUpdate={handleStoryUpdate}
                  onChange={handleVisibilityChange}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
              onClick={onClose}
              disabled={saving}
              type="button"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium flex items-center gap-2"
              onClick={handleSave}
              disabled={saving}
              type="button"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiSave} className="text-lg" /> Save Changes
                </>
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditStoryModal;